import type { OrderStatus, WorkflowTemplateId } from "../contracts"
import type { LabFulfillmentMethod, PatientWorkflowStateValue, PatientWorkflowView } from "../machine/patient-order-machine"
import { getWorkflowTemplate } from "../templates/workflow-templates"

type WorkflowProgressView = Pick<PatientWorkflowView, "currentStatus" | "progressPercent">

type OrderProgressRailStepId =
	| "intake"
	| "payment"
	| "lab-ordered"
	| "kit-shipped"
	| "kit-delivered"
	| "sample-sent"
	| "sample-delivered"
	| "upload-labs"
	| "lab-results"
	| "visit"
	| "prescription"
	| "preparing"
	| "shipped"
	| "delivered"

type OrderProgressRailStep = {
	readonly id: OrderProgressRailStepId
	readonly label: string
}

export type OrderProgressRail = {
	readonly steps: readonly string[]
	/** Integer values sit on a step; fractional values represent progress along its outgoing connector. */
	readonly currentStep: number
}

export type OrderProgressRailArgs = {
	readonly workflowTemplateId: WorkflowTemplateId
	readonly workflowTemplateVersion: number
	readonly labFulfillmentMethod: LabFulfillmentMethod | null
	readonly kitLabUploadPayment?: "paid" | "unpaid" | undefined
	readonly currentStatus: OrderStatus
	readonly workflowState: PatientWorkflowStateValue
	readonly isFulfillmentOnHold?: boolean
}

const INTAKE_STEP = { id: "intake", label: "Intake Started" } as const satisfies OrderProgressRailStep
const PAYMENT_STEP = { id: "payment", label: "Payment" } as const satisfies OrderProgressRailStep
const LAB_ORDERED_STEP = { id: "lab-ordered", label: "Lab Kit Ordered" } as const satisfies OrderProgressRailStep
const KIT_SHIPPED_STEP = { id: "kit-shipped", label: "Lab Kit Shipped" } as const satisfies OrderProgressRailStep
const KIT_DELIVERED_STEP = { id: "kit-delivered", label: "Lab Kit Delivered" } as const satisfies OrderProgressRailStep
const SAMPLE_SENT_STEP = { id: "sample-sent", label: "Sample Sent" } as const satisfies OrderProgressRailStep
const SAMPLE_DELIVERED_STEP = { id: "sample-delivered", label: "Sample Delivered" } as const satisfies OrderProgressRailStep
const UPLOAD_LABS_STEP = { id: "upload-labs", label: "Upload Labs" } as const satisfies OrderProgressRailStep
const LAB_RESULTS_STEP = { id: "lab-results", label: "Lab Results" } as const satisfies OrderProgressRailStep
const VISIT_STEP = { id: "visit", label: "Book Visit" } as const satisfies OrderProgressRailStep
const PRESCRIPTION_STEP = { id: "prescription", label: "Rx Review" } as const satisfies OrderProgressRailStep
const PREPARING_STEP = { id: "preparing", label: "Preparing prescription" } as const satisfies OrderProgressRailStep
const SHIPPED_STEP = { id: "shipped", label: "Shipped" } as const satisfies OrderProgressRailStep
const DELIVERED_STEP = { id: "delivered", label: "Delivered" } as const satisfies OrderProgressRailStep

const POST_LAB_STEPS = [VISIT_STEP, PRESCRIPTION_STEP, PREPARING_STEP, SHIPPED_STEP, DELIVERED_STEP] as const

function getRailSteps(args: Pick<OrderProgressRailArgs, "workflowTemplateId" | "workflowTemplateVersion" | "labFulfillmentMethod" | "kitLabUploadPayment">): readonly OrderProgressRailStep[] {
	const template = getWorkflowTemplate(args.workflowTemplateId, args.workflowTemplateVersion)
	if (args.labFulfillmentMethod === "upload_existing_results") {
		if (args.kitLabUploadPayment === "paid") return [INTAKE_STEP, PAYMENT_STEP, UPLOAD_LABS_STEP, LAB_RESULTS_STEP, ...POST_LAB_STEPS]
		return [INTAKE_STEP, UPLOAD_LABS_STEP, LAB_RESULTS_STEP, PAYMENT_STEP, ...POST_LAB_STEPS]
	}
	const shippedKitSteps = [
		LAB_ORDERED_STEP,
		KIT_SHIPPED_STEP,
		KIT_DELIVERED_STEP,
		SAMPLE_SENT_STEP,
		SAMPLE_DELIVERED_STEP,
		LAB_RESULTS_STEP,
	] as const
	if (template.policies.shipKitPaymentTiming === "after_lab_results") {
		return [INTAKE_STEP, ...shippedKitSteps, PAYMENT_STEP, ...POST_LAB_STEPS]
	}
	return [INTAKE_STEP, PAYMENT_STEP, ...shippedKitSteps, ...POST_LAB_STEPS]
}

function getRailStepId(args: OrderProgressRailArgs): OrderProgressRailStepId {
	if (args.workflowState === "lab-upload") return "upload-labs"
	if (args.workflowState === "LAB_MANUAL_REVIEW_PENDING") return "lab-results"
	if (args.workflowState === "clinical-review-completed") return "visit"
	if (args.workflowState === "lab-results-approved-payment-ready") return "payment"

	if (
		args.currentStatus === "CREATED" ||
		args.currentStatus === "QUESTIONNAIRE_SUBMITTED" ||
		args.currentStatus === "ELIGIBILITY_FAILED" ||
		args.currentStatus === "ELIGIBILITY_PASSED" ||
		args.currentStatus === "CORRECTION_REQUIRED" ||
		args.currentStatus === "CORRECTION_IN_PROGRESS" ||
		args.currentStatus === "CORRECTION_SUBMITTED" ||
		args.currentStatus === "CORRECTION_REJECTED" ||
		args.currentStatus === "CORRECTION_APPROVED" ||
		args.currentStatus === "CANCELLED" ||
		args.currentStatus === "EXPIRED"
	) {
		return "intake"
	}
	if (
		args.currentStatus === "PAYMENT_PENDING" ||
		args.currentStatus === "PAYMENT_APPROVED" ||
		args.currentStatus === "PAYMENT_FAILED" ||
		args.currentStatus === "PAYMENT_RETRY_ALLOWED" ||
		args.currentStatus === "PAYMENT_RETRY_EXHAUSTED" ||
		args.currentStatus === "PAYMENT_REFUNDED"
	) {
		return "payment"
	}
	if (args.currentStatus === "LAB_REQUIRED" || args.currentStatus === "LAB_ORDERED") {
		return args.labFulfillmentMethod === "upload_existing_results" ? "upload-labs" : "lab-ordered"
	}
	if (args.currentStatus === "LAB_KIT_SHIPPED") return "kit-shipped"
	if (args.currentStatus === "LAB_KIT_DELIVERED") return "kit-delivered"
	if (args.currentStatus === "LAB_SAMPLE_IN_TRANSIT") return "sample-sent"
	if (args.currentStatus === "LAB_SAMPLE_DELIVERED" || args.currentStatus === "LAB_SAMPLE_RECEIVED") return "sample-delivered"
	if (
		args.currentStatus === "LAB_RESULTS_PENDING" ||
		args.currentStatus === "LAB_RESULTS_REJECTED" ||
		args.currentStatus === "LAB_RESULTS_APPROVED"
	) {
		return "lab-results"
	}
	if (args.currentStatus === "LAB_FAILED" || args.currentStatus === "LAB_REORDER_REQUIRED") {
		return args.labFulfillmentMethod === "upload_existing_results" ? "upload-labs" : "lab-ordered"
	}
	if (
		args.currentStatus === "VISIT_CONFIRMED" ||
		args.currentStatus === "VISIT_CANCELLED" ||
		args.currentStatus === "VISIT_COMPLETED"
	) {
		return "visit"
	}
	if (args.currentStatus === "PRESCRIPTION_APPROVED") return "preparing"
	if (args.currentStatus === "PRESCRIPTION_REJECTED") return "prescription"
	if (args.currentStatus === "PHARMACY_DISPATCHED" || args.currentStatus === "PHARMACY_FAILED") return "shipped"
	if (
		args.currentStatus === "PHARMACY_DELIVERED" ||
		args.currentStatus === "REFILL_ELIGIBLE" ||
		args.currentStatus === "REFILL_UPCOMING" ||
		args.currentStatus === "REFILL_LOCKED" ||
		args.currentStatus === "REFILL_PAYMENT_PENDING" ||
		args.currentStatus === "REFILL_RELEASED" ||
		args.currentStatus === "REFILL_CANCELLED" ||
		args.currentStatus === "COMPLETED"
	) {
		return "delivered"
	}
	throw new Error(`Order status ${args.currentStatus} does not map to the progress rail`)
}

export function getOrderProgressRail(args: OrderProgressRailArgs): OrderProgressRail {
	const railSteps = getRailSteps(args)
	const currentStepId = args.isFulfillmentOnHold ? "preparing" : getRailStepId(args)
	const currentStepIndex = railSteps.findIndex((step) => step.id === currentStepId)
	if (currentStepIndex < 0) {
		throw new Error(`Progress rail step ${currentStepId} does not exist for the active workflow route`)
	}
	const visitStepIndex = railSteps.findIndex((step) => step.id === "visit")
	let visitLabel = "Book Visit"
	if (args.currentStatus === "VISIT_CONFIRMED") visitLabel = "Booked Visit"
	else if (args.currentStatus === "VISIT_CANCELLED") visitLabel = "Rebook Visit"
	else if (args.currentStatus === "VISIT_COMPLETED" || currentStepIndex > visitStepIndex) visitLabel = "Visit Attended"
	const steps = railSteps.map((step) => {
		if (step.id === "visit") return visitLabel
		if (step.id === "preparing" && args.isFulfillmentOnHold) return "On hold"
		return step.label
	})
	let currentStep = currentStepIndex
	if (!args.isFulfillmentOnHold) {
		if (args.currentStatus === "VISIT_CONFIRMED") currentStep += 0.5
		else if (args.currentStatus === "VISIT_COMPLETED") currentStep += 1
	}
	return { steps, currentStep }
}

const ERROR_STATUSES: ReadonlySet<OrderStatus> = new Set([
	"ELIGIBILITY_FAILED",
	"PAYMENT_FAILED",
	"PAYMENT_RETRY_EXHAUSTED",
	"LAB_RESULTS_REJECTED",
	"LAB_FAILED",
	"LAB_REORDER_REQUIRED",
	"VISIT_CANCELLED",
	"PRESCRIPTION_REJECTED",
	"PHARMACY_FAILED",
	"CORRECTION_REQUIRED",
	"CORRECTION_REJECTED",
	"CANCELLED",
	"EXPIRED",
])

export const SUCCESS_STATUSES: ReadonlySet<OrderStatus> = new Set([
	"PHARMACY_DELIVERED",
	"COMPLETED",
])

export type ProgressState = {
	readonly percent: number
	readonly isError: boolean
	readonly isTerminalSuccess: boolean
}

export function getProgressState(workflowView: WorkflowProgressView): ProgressState {
	if (
		!Number.isInteger(workflowView.progressPercent) ||
		workflowView.progressPercent < 0 ||
		workflowView.progressPercent > 100
	) {
		throw new Error(`Workflow progress must be an integer from 0 to 100, received ${workflowView.progressPercent}`)
	}
	return {
		percent: workflowView.progressPercent,
		isError: ERROR_STATUSES.has(workflowView.currentStatus),
		isTerminalSuccess: workflowView.currentStatus === "COMPLETED",
	}
}
